import {
  RimsStep,
  RimsStepProperty,
  RimsStepPropertySchema,
  RimsStepPropertyTree,
  RimsStepSchema,
  SchemaType,
} from '@app/models/rims.model';
import type { RimsFormDescriptor } from '@app/pages/incidents/details-page/rims/rims-edit-panel/rims-form/rims-form-builder/rims-form-builder.service';
import { formatAsDateTimeStringIgnoringTheTime } from '../datetime/datetime.utils';

/**
 * Applies touched form control values to a RIMS step model.
 *
 * Paths come from form metadata and are slash-separated.
 * Example:
 * - formPath: "set/0"
 * - control key: "damagesSought/amount"
 * - resolved path: "set/0/damagesSought/amount"
 */
export const updateRimsStepFromForms = (rimsStep: RimsStep, forms: RimsFormDescriptor[]): void => {
  for (const form of forms) {
    Object.entries(form.formGroup.controls).map(([key, control]) => {
      if (!control.touched) {
        return;
      }

      // Comparing the last value from a path (economicLoss/amount => we check only the "amount" part instead of checking contains on the whole path)
      const isSelfControl = form.formPath.split('/').at(-1) === key;
      const controlPath = isSelfControl ? form.formPath : `${form.formPath}/${key}`;
      const updateValue =
        control.value instanceof Date ? formatAsDateTimeStringIgnoringTheTime(control.value) : control.value;
      setValueAtPathIfExists(rimsStep, controlPath, updateValue);
    });
  }
};

type PathSegment = string | number;

/**
 * Converts numeric path segments to numbers so array access is explicit.
 * Example: "0" -> 0, "product" -> "product"
 */
const toPathSegment = (segment: string): PathSegment => {
  return /^\d+$/.test(segment) ? Number(segment) : segment;
};

/**
 * Normalizes slash-separated paths used in RIMS fields.
 * Example: "set/0/data/dataType" -> ["set", 0, "data", "dataType"]
 */
const parsePath = (path: string): PathSegment[] => {
  return path
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .map((segment) => toPathSegment(segment));
};

const isObjectLike = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

/**
 * Checks whether a specific segment exists on the current container.
 * - Numeric segment means array index access.
 * - String segment means object property access.
 */
const hasOwnPathSegment = (container: unknown, segment: PathSegment): boolean => {
  if (typeof segment === 'number') {
    return Array.isArray(container) && segment >= 0 && segment < container.length;
  }

  return isObjectLike(container) && Object.hasOwn(container, segment);
};

/**
 * Returns true only when every segment in the path exists.
 * Does not attempt to create missing nodes.
 */
export const hasPath = (target: unknown, path: string): boolean => {
  const segments = parsePath(path);
  if (segments.length === 0) {
    return false;
  }

  let current: unknown = target;
  for (const segment of segments) {
    if (!hasOwnPathSegment(current, segment)) {
      return false;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  return true;
};

/**
 * Reads a value from a slash-separated path.
 * Returns undefined when any segment is missing.
 */
export const getValueAtPath = (target: unknown, path: string): unknown => {
  const segments = parsePath(path);
  if (segments.length === 0) {
    return undefined;
  }

  let current: unknown = target;
  for (const segment of segments) {
    if (!hasOwnPathSegment(current, segment)) {
      return undefined;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  return current;
};

/**
 * Sets the value at the specified path only if the complete path already exists.
 * Does not create missing objects/arrays.
 *
 * Returns true when value was written, otherwise false.
 * Example:
 * - setValueAtPathIfExists(step, "set/0/data/dataType", "Some Value")
 */
export const setValueAtPathIfExists = (target: RimsStep, path: string, value: unknown): boolean => {
  const segments = parsePath(path);
  if (segments.length === 0) {
    return false;
  }

  if (segments.length === 1) {
    const leafSegment = segments[0];
    if (!hasOwnPathSegment(target, leafSegment)) {
      return false;
    }

    (target as Record<string, unknown>)[leafSegment] = value as RimsStepProperty;
    return true;
  }

  let current: unknown = target;
  for (let index = 0; index < segments.length - 1; index++) {
    const segment = segments[index];

    if (!hasOwnPathSegment(current, segment)) {
      return false;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  const leafSegment = segments[segments.length - 1];
  if (!hasOwnPathSegment(current, leafSegment)) {
    return false;
  }

  (current as Record<string, unknown>)[leafSegment] = value as RimsStepProperty;
  return true;
};

/**
 * Creates a new RIMS step object with default values derived from schema.
 */
export const createRimsStepFromSchema = (schema: RimsStepSchema, populateArrayWithASingleObject: boolean): RimsStep => {
  const step: RimsStep = { $type: schema.stepId };
  for (const [key, prop] of Object.entries(schema.properties)) {
    step[key] = createRimsStepProperty(prop, populateArrayWithASingleObject);
  }

  return step;
};

export const createRimsStepProperty = (
  rimsStepProperty: RimsStepPropertySchema,
  populateArrayWithASingleObject: boolean,
): RimsStepProperty => {
  const propertyType = Array.isArray(rimsStepProperty.type) ? rimsStepProperty.type[0] : rimsStepProperty.type;
  let value: RimsStep[string];

  if (rimsStepProperty.enum) {
    value = null; // enum: no initial value, user picks from the list
  } else if (rimsStepProperty.format === 'uuid') {
    value = crypto.randomUUID(); // uuid: generate a new UUID V4
  } else {
    switch (propertyType) {
      case 'object':
        value = rimsStepProperty.properties
          ? (Object.fromEntries(
              Object.entries(rimsStepProperty.properties).map(([k, v]) => [
                k,
                createRimsStepProperty(v, populateArrayWithASingleObject),
              ]),
            ) as RimsStep[string])
          : null;
        break;
      case 'array':
        value = populateArrayWithASingleObject
          ? ([
              Object.fromEntries(
                Object.entries(rimsStepProperty.items!.properties!).map(([k, v]) => [
                  k,
                  createRimsStepProperty(v, populateArrayWithASingleObject),
                ]),
              ),
            ] as RimsStep[string])
          : [];
        break;
      default:
        value = null;
    }
  }

  return value;
};

/**
 * Returns the primary schema type, supporting both "type: string" and "type: string[]".
 */
export const getPropertyType = (rimsStepPropertySchema: RimsStepPropertySchema): SchemaType | undefined =>
  Array.isArray(rimsStepPropertySchema.type) ? rimsStepPropertySchema.type[0] : rimsStepPropertySchema.type;

/**
 * Helper for UI/form generation: identifies container-like schema nodes.
 */
export const isPropertyTypeObjectOrArray = (propertySchema: RimsStepPropertySchema): boolean => {
  const propertyType = getPropertyType(propertySchema);

  return propertyType === 'object' || propertyType === 'array';
};

/**
 * Capitalizes the first letter of every slash-separated segment of a RIMS path.
 * Backend-stored field reference names use PascalCase segments (e.g. "EconomicLoss/Amount"),
 * while the front-end schema/property paths are camelCase (e.g. "economicLoss/amount").
 * Only apply this to property-name segments, never to ids/guids (e.g. RIMS set item ids).
 */
export const capitalizeRimsPath = (path: string): string => {
  return path
    .split('/')
    .map((segment) => (segment.length > 0 ? segment.charAt(0).toUpperCase() + segment.slice(1) : segment))
    .join('/');
};

/**
 * Resolves the property schema at a slash-separated path (array indices are skipped into `items`).
 * Example: "set/0/data/dataType" walks properties.set -> items -> properties.data -> properties.dataType.
 */
export const getRimsPropertySchemaAtPath = (
  properties: Record<string, RimsStepPropertySchema> | undefined,
  path: string,
): RimsStepPropertySchema | null => {
  const segments = parsePath(path);
  if (segments.length === 0) {
    return null;
  }

  let currentProperties = properties;
  let currentSchema: RimsStepPropertySchema | null = null;

  for (const segment of segments) {
    if (typeof segment === 'number') {
      // Array index segment: move into the array's item schema, property lookup resumes from there.
      if (!currentSchema?.items) {
        return null;
      }

      currentSchema = currentSchema.items;
      currentProperties = currentSchema.properties;
      continue;
    }

    currentSchema = currentProperties?.[segment] ?? null;
    if (!currentSchema) {
      return null;
    }

    currentProperties = currentSchema.properties;
  }

  return currentSchema;
};

export const isRimsSetItemField = (field: RimsStepPropertyTree): boolean => {
  return field.objectPath.includes('set/') && field.name === 'id';
};
